import React from 'react'
import { Flex, Box, styled, css } from '@chilipiper/design-system'
import { AlertBanner } from '@chilipiper/design-system/src/new'
import { Status } from '../../xmpp-client/XMPPClient'
import { useReconnectingStatus } from './useReconnectingStatus'

interface Props {
  reconnectingStatus: Status
}

const Reconnecting = () => (
  <Wrapper>
    <AlertBanner type='inline' title='Restoring connection' variant='neutral' />
  </Wrapper>
)

const Connecting = () => (
  <Wrapper>
    <AlertBanner type='inline' title='Connecting' variant='neutral' />
  </Wrapper>
)

const Reconnected = () => (
  <Wrapper>
    <AlertBanner type='inline' title='Connection restored' variant='success' />
  </Wrapper>
)

const Error = () => (
  <Wrapper>
    <AlertBanner
      type='inline'
      title='Error occurred while loading new messages. Try refreshing the page.'
      variant='caution'
    />
  </Wrapper>
)

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <Box flexGrow={1} overflow='hidden' borderRadius={12} mx={6} mt={3}>
    {children}
  </Box>
)

export const ReconnectingStatusBanner = ({ reconnectingStatus }: Props) => {
  switch (reconnectingStatus) {
    case 'ERROR':
      return <Error />
    case 'CONNECTED':
      return <Reconnected />
    case 'RECONNECTING':
      return <Reconnecting />
    case 'CONNECTING':
    case 'DISCONNECTED':
      return <Connecting />
    default:
      return null
  }
}

const ReconnectingStatusWrapper = styled(Flex)<{ opened: boolean; openHeight: string }>`
  transition:
    height 0.5s ease-in-out,
    opacity 0.5s ease-in-out;
  opacity: 0;
  pointer-events: none;
  height: 0;

  ${({ opened, openHeight }) => {
    return opened
      ? css`
          pointer-events: auto;
          height: ${openHeight};
          opacity: 1;
        `
      : ''
  }}
`

export const ReconnectingStatus = ({ reconnectingStatus }: Props) => {
  const { opened, delayedReconnectingStatus } = useReconnectingStatus(reconnectingStatus)

  return (
    <ReconnectingStatusWrapper
      opened={opened}
      openHeight={delayedReconnectingStatus === 'ERROR' ? '80px' : '60px'}
    >
      <ReconnectingStatusBanner reconnectingStatus={delayedReconnectingStatus} />
    </ReconnectingStatusWrapper>
  )
}
